import React, { useCallback, useEffect, useMemo } from 'react';
import { HevoEntity } from '../../../core/interfaces/hevo-entity';
import { MultiRegionService } from '../../../auth/services/multi-region.service';
import { TroubleshootGuideService } from '../../../core/services/troubleshoot-guide.service';
import {
  ERROR_SNACKBAR_SUPPORT_CLICK,
  ERROR_SNACKBAR_TROUBLESHOOT_BUTTON_CLICK,
  ERROR_SNACKBAR_TROUBLESHOOT_BUTTON_SHOWN
} from '../../tracking/entityErrorPane';
import useAnalyticsTracker from '../../../analytics/hooks/useAnalyticsTracker';
import useService from '../../../injector/hooks/useService';
import { getDataIdGenerator } from '../../../core/utils/generateDataId';
import { HdSupportButton } from '../../../support/components/support-button/SupportButton';
import { HdButton, HdDocLink, HdPane } from '../../../components';
import { HdPaneProps } from '../hd-pane/HdPane';

export interface EntityErrorPaneProps extends Omit<Omit<HdPaneProps, 'children'>, 'variant'> {
  dataId: string;
  reason: string;
  entity: HevoEntity;
  entitySeqId: number;
  docLink?: string;
  docLinkText?: string;
  showSupportBtn?: boolean;
  getSupportMessage?: Function;
  troubleShootingActions?: {
    docLink: string;
  };
}

export function EntityErrorPane({
  dataId,
  reason,
  entity,
  entitySeqId,
  docLink,
  docLinkText,
  troubleShootingActions,
  getSupportMessage,
  showSupportBtn = true,
  ...others
}: EntityErrorPaneProps) {
  const dataIdGenerator = useCallback(getDataIdGenerator(dataId, 'entity-error'), [dataId]);

  const troubleshootGuideService = useService(TroubleshootGuideService);
  const { eventTrack } = useAnalyticsTracker();
  const multiRegionService = useService(MultiRegionService);

  const troubleShootShown = !!troubleShootingActions?.docLink;
  const region = multiRegionService.isMultiRegionUser() ? multiRegionService.getSelectedRegionValue()?.display_name : null;

  useEffect(() => {
    eventTrack({
      action: ERROR_SNACKBAR_TROUBLESHOOT_BUTTON_SHOWN,
      properties: troubleShootingActions
    });
  }, [ troubleShootShown ]);

  const supportMessageToRender = useMemo(() => {
    if (typeof getSupportMessage === 'function') {
      return getSupportMessage({
        entity,
        reason,
        entitySeqId,
        region
      });
    }

    return `Hey I am getting following error \`${reason}\`. Can you help?`;
  }, [ getSupportMessage, entity, entitySeqId, reason, region ]);

  const showTroubleShootingDrawer = () => {
    troubleshootGuideService.showTroubleshootGuide(troubleShootingActions?.docLink);

    eventTrack({
      action: ERROR_SNACKBAR_TROUBLESHOOT_BUTTON_CLICK,
      properties: troubleShootingActions
    });
  };

  const supportClick = () => {
    eventTrack({
      action: ERROR_SNACKBAR_SUPPORT_CLICK,
      properties: {
        entity,
        reason,
        entitySeqId,
        region,
        docLink: {
          url: docLink,
          text: docLinkText
        },
        troubleShootingActions
      }
    });
  };

  return (
    <HdPane
      {...others}
      dataId={dataIdGenerator('')}
      variant='error-faded-2'
      icon='error-filled'
    >
      <div className='d-flex align-items-start break-word justify-between'>
        <div className='text-default'>
          {reason}

          {docLink ? (
            <HdDocLink
              className='ml-1'
              dataId={dataIdGenerator('')}
              label={docLinkText}
              docLink={docLink}
              section='entity_error'
            />
          ) : null}
        </div>

        <div className='center-flex-row'>
          {troubleShootingActions?.docLink ? (
            <HdButton
              className='ml-2'
              palette='error'
              size='sm'
              icon='support'
              variation='outline'
              inlineText
              onClick={showTroubleShootingDrawer}
              dataId={dataIdGenerator('chat-support')}
            >
              Troubleshoot
            </HdButton>
          ) : null}

          {showSupportBtn ? (
            <HdSupportButton
              className='ml-2'
              palette='error'
              size='sm'
              icon='support'
              variation='outline'
              inlineText
              onClick={supportClick}
              dataId={dataIdGenerator('chat-support')}
              Component={HdButton}
              message={supportMessageToRender}
            >
              Chat with Support
            </HdSupportButton>
          ) : null}
        </div>
      </div>
    </HdPane>
  );
}
